// src/components/DeckBuilder.js
import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import { getMyCardTier, getNextMyCardTier, sumBaseStats } from '../utils';
import { doc, updateDoc } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import toast, { Toaster } from 'react-hot-toast';
import Swal from 'sweetalert2';

const FORMATIONS = {
    '4-4-2': [
        { id: 'FW1', role: 'FW', line: 'FW', label: 'CF' }, { id: 'FW2', role: 'FW', line: 'FW', label: 'ST' },
        { id: 'MF1', role: 'MF', line: 'MF', label: 'LSH' }, { id: 'MF2', role: 'MF', line: 'MF', label: 'CMF' }, { id: 'MF3', role: 'MF', line: 'MF', label: 'CMF' }, { id: 'MF4', role: 'MF', line: 'MF', label: 'RSH' },
        { id: 'DF1', role: 'DF', line: 'DF', label: 'LSB' }, { id: 'DF2', role: 'DF', line: 'DF', label: 'CB' }, { id: 'DF3', role: 'DF', line: 'DF', label: 'CB' }, { id: 'DF4', role: 'DF', line: 'DF', label: 'RSB' },
        { id: 'GK1', role: 'GK', line: 'GK', label: 'GK' }
    ],
    '4-3-3': [
        { id: 'FW1', role: 'FW', line: 'FW', label: 'LWG' }, { id: 'FW2', role: 'FW', line: 'FW', label: 'CF' }, { id: 'FW3', role: 'FW', line: 'FW', label: 'RWG' },
        { id: 'MF1', role: 'MF', line: 'AMF', label: 'CMF' }, { id: 'MF3', role: 'MF', line: 'AMF', label: 'CMF' },
        { id: 'MF2', role: 'MF', line: 'DMF', label: 'DMF' },
        { id: 'DF1', role: 'DF', line: 'DF', label: 'LSB' }, { id: 'DF2', role: 'DF', line: 'DF', label: 'CB' }, { id: 'DF3', role: 'DF', line: 'DF', label: 'CB' }, { id: 'DF4', role: 'DF', line: 'DF', label: 'RSB' },
        { id: 'GK1', role: 'GK', line: 'GK', label: 'GK' }
    ],
    '3-5-2': [
        { id: 'FW1', role: 'FW', line: 'FW', label: 'CF' }, { id: 'FW2', role: 'FW', line: 'FW', label: 'ST' },
        { id: 'MF1', role: 'MF', line: 'AMF', label: 'LSH' }, { id: 'MF2', role: 'MF', line: 'AMF', label: 'OMF' }, { id: 'MF5', role: 'MF', line: 'AMF', label: 'RSH' },
        { id: 'MF3', role: 'MF', line: 'DMF', label: 'DMF' }, { id: 'MF4', role: 'MF', line: 'DMF', label: 'DMF' },
        { id: 'DF1', role: 'DF', line: 'DF', label: 'CB' }, { id: 'DF2', role: 'DF', line: 'DF', label: 'CB' }, { id: 'DF3', role: 'DF', line: 'DF', label: 'CB' },
        { id: 'GK1', role: 'GK', line: 'GK', label: 'GK' }
    ],
    '4-2-3-1': [
        { id: 'FW1', role: 'FW', line: 'FW', label: 'CF' },
        { id: 'MF1', role: 'MF', line: 'AMF', label: 'LSH' }, { id: 'MF2', role: 'MF', line: 'AMF', label: 'OMF' }, { id: 'MF3', role: 'MF', line: 'AMF', label: 'RSH' },
        { id: 'MF4', role: 'MF', line: 'DMF', label: 'DMF' }, { id: 'MF5', role: 'MF', line: 'DMF', label: 'DMF' },
        { id: 'DF1', role: 'DF', line: 'DF', label: 'LSB' }, { id: 'DF2', role: 'DF', line: 'DF', label: 'CB' }, { id: 'DF3', role: 'DF', line: 'DF', label: 'CB' }, { id: 'DF4', role: 'DF', line: 'DF', label: 'RSB' },
        { id: 'GK1', role: 'GK', line: 'GK', label: 'GK' }
    ]
};

const ROLE_MAP = {
    'CF': 'FW', 'ST': 'FW', 'LWG': 'FW', 'RWG': 'FW', 'WG': 'FW', 'FW': 'FW',
    'OMF': 'MF', 'CMF': 'MF', 'DMF': 'MF', 'LSH': 'MF', 'RSH': 'MF', 'SMF': 'MF', 'MF': 'MF',
    'CB': 'DF', 'LSB': 'DF', 'RSB': 'DF', 'DF': 'DF',
    'GK': 'GK', 'FP': 'FP', '未設定': 'FP'
};

// ★ソート機能用の順位づけ
// ★ カード上のバッジに出す短い表記。
//   内部ID（MY_ACE など）をそのまま出すと幅を超えるし、
//   ユーザーにとって意味が伝わらないため。
const RARITY_LABEL = {
    MY: '⭐ MY',
    MY_PLUS: '⭐ MY+',
    MY_PP: '⭐ MY++',
    MY_ACE: '👑 ACE',
    MY_LEGEND: '🔥 LEGEND'
};
const rarityLabel = (card) => {
    const rid = card?.rarity?.id;
    return RARITY_LABEL[rid] || rid || '';
};

const RARITY_ORDER = { 'SSR': 5, 'SR': 4, 'S': 3, 'R': 2, 'N': 1,
    // ★ マイカードは常に先頭に表示する
    'MY': 6, 'MY_PLUS': 6, 'MY_PP': 6, 'MY_ACE': 6, 'MY_LEGEND': 6 };
const POSITION_ORDER = { 'FW': 4, 'MF': 3, 'DF': 2, 'GK': 1, 'FP': 0 };

const DeckBuilder = ({ user, users = [], onBack }) => {
    const [sortMode, setSortMode] = useState('rarity'); // rarity | power | position
    const [formation, setFormation] = useState('4-4-2');
    const [deck, setDeck] = useState({});
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (user && user.deck) setDeck(user.deck);
        if (user && user.formation && FORMATIONS[user.formation]) {
            setFormation(user.formation);
        }
    }, [user]);

    // ★ 引退カードの自動還元。
    //   引退判定（isRetired / 180日未ログイン）と還元額の算出は
    //   すべて Cloud Functions 側で行う。
    //   以前はクライアントの sellPrices を書き換えるだけで
    //   任意の金額を得られた。
    useEffect(() => {
        if (!user || !user.userId || !user.cards || user.cards.length === 0) return;
        if (window._deckRefundProcessing) return;

        const run = async () => {
            window._deckRefundProcessing = true;
            try {
                const fns = getFunctions(getApp(), 'asia-northeast1');
                const res = await httpsCallable(fns, 'refundRetiredCards')({});
                const d = res?.data || {};

                if (d.refund > 0) {
                    // 画面の即時反映（次の onSnapshot で上書きされる）
                    setDeck(prev => {
                        const updated = { ...prev };
                        Object.keys(updated).forEach(slot => {
                            if (updated[slot] && !(user.cards || []).some(c => c.id === updated[slot].id)) {
                                delete updated[slot];
                            }
                        });
                        return updated;
                    });

                    Swal.fire({
                        title: '💳 カード自動移籍',
                        html: `所持していた選手が引退したため、カードが自動移籍されました。<br><br><b>対象カード:</b><br><small>${(d.names || []).join(', ')}</small><br><br><b>移籍金獲得:</b> <span style="color:#e65100;font-weight:bold;">${d.refund} RBコイン</span>`,
                        icon: 'info',
                        confirmButtonColor: '#1976d2'
                    });
                }
            } catch (e) {
                console.error("自動還元エラー", e?.code || '', e?.message || e);
            } finally {
                window._deckRefundProcessing = false;
            }
        };

        run();
    }, [user?.userId]);

    const myCards = useMemo(() => {
        const cards = user?.cards ? [...user.cards] : [];
        
        let updatedCards = cards.map(card => {
            const latestUser = users.find(u => u.userId === card.userId);
            if (latestUser) {
                const latestStats = latestUser.baseStats || { offense: 20, defense: 20, stamina: 20, speed: 20, technique: 20 };
                const mainPos = latestUser.mainPosition || '未設定';
                const subPos = latestUser.subPosition || '未設定';
                const oldPos = latestUser.position || 'FP';
                
                let displayPos = subPos !== '未設定' ? subPos : (mainPos !== '未設定' ? mainPos : (oldPos !== 'FP' ? oldPos : 'FP'));
                if (displayPos === '未設定') displayPos = 'FP';

                // ★ レアリティ倍率と最低保証を適用してから反映する。
                //   以前は latestStats（本人の生の baseStats）をそのまま代入していたため、
                //   N でも SSR でも同じ数値になってしまっていた。
                //   （デッキに入っている間は deck 側の値を見るので正しく、
                //     一度外して入れ直すと下がる、という現象になっていた）
                const rar = card.rarity || {};
                const mul = Number(rar.multiplier) || 1;
                const minStat = Number(rar.minStat) || 0;
                const penalty = card.isPositionSet === false ? 0.5 : 1;
                const calc = (v) => Math.max(Math.floor((Number(v) || 0) * mul * penalty), minStat);

                return {
                    ...card,
                    name: latestUser.displayName || card.name,
                    pictureUrl: latestUser.pictureUrl || card.pictureUrl,
                    position: displayPos,
                    stats: {
                        offense: calc(latestStats.offense),
                        defense: calc(latestStats.defense),
                        stamina: calc(latestStats.stamina),
                        speed: calc(latestStats.speed),
                        technique: calc(latestStats.technique)
                    }
                };
            }
            return card;
        });

        // 自分のカードを追加
        if (user && user.userId) {
            const myBaseStats = user.baseStats || { offense: 20, defense: 20, stamina: 20, speed: 20, technique: 20 };
            const mainPos = user.mainPosition || '未設定';
            const subPos = user.subPosition || '未設定';
            const oldPos = user.position || 'FP';
            let displayPos = subPos !== '未設定' ? subPos : (mainPos !== '未設定' ? mainPos : (oldPos !== 'FP' ? oldPos : 'FP'));
            if (displayPos === '未設定') displayPos = 'FP';

            // ★ 特訓で振り分けた合計に応じて、マイカードのランクと倍率が上がる。
            //   以前は baseStats をそのまま（倍率1.0）使っていたため、
            //   鑑えても他人の SSR（×2.0）に勝てない状態だった。
            const myTier = getMyCardTier(myBaseStats);
            const myCalc = (v) => Math.floor((Number(v) || 0) * myTier.multiplier);

            updatedCards.push({
                id: `my_card_${user.userId}`, 
                userId: user.userId,
                name: user.displayName || '自分',
                pictureUrl: user.pictureUrl || null,
                position: displayPos,
                rarity: {
                    id: myTier.id,
                    name: myTier.name,
                    icon: myTier.icon,
                    multiplier: myTier.multiplier,
                    captainBonus: myTier.captainBonus,
                    color: myTier.color
                },
                isMyCard: true,
                stats: {
                    offense: myCalc(myBaseStats.offense),
                    defense: myCalc(myBaseStats.defense),
                    stamina: myCalc(myBaseStats.stamina),
                    speed: myCalc(myBaseStats.speed),
                    technique: myCalc(myBaseStats.technique)
                }
            });
        }

        // ★ここでソートを実行！
        updatedCards.sort((a, b) => {
            if (sortMode === 'rarity') {
                return (RARITY_ORDER[b.rarity.id] || 0) - (RARITY_ORDER[a.rarity.id] || 0);
            } else if (sortMode === 'power') {
                const getP = (c) => (c.stats.offense + c.stats.defense + c.stats.stamina + c.stats.speed + c.stats.technique);
                return getP(b) - getP(a);
            } else if (sortMode === 'position') {
                const getPosRank = (pos) => POSITION_ORDER[ROLE_MAP[pos]] || 0;
                return getPosRank(b.position) - getPosRank(a.position);
            }
            return 0;
        });

        return updatedCards;
    }, [user, users, sortMode]);

    const getAptitudeMultiplier = (cardPos, slotRole, slotLabel) => {
        if (cardPos === slotLabel) return 1.0; 
        const cardMainRole = ROLE_MAP[cardPos] || 'FP';
        if (cardMainRole === slotRole) return 0.8; 
        if (cardMainRole === 'FP' && slotRole !== 'GK') return 0.8; 
        if (cardMainRole === 'GK' && slotRole !== 'GK') return 0.1; 
        if (cardMainRole !== 'GK' && slotRole === 'GK') return 0.1; 
        return 0.5; 
    };

    const teamStats = useMemo(() => {
        let stats = { offense: 0, defense: 0, stamina: 0, speed: 0, technique: 0, total: 0 };
        if (!deck || !FORMATIONS[formation]) return stats;
        FORMATIONS[formation].forEach(slot => {
            const card = deck[slot.id];
            if (card && card.stats) {
                const mult = getAptitudeMultiplier(card.position, slot.role, slot.label);
                stats.offense += Math.floor(card.stats.offense * mult);
                stats.defense += Math.floor(card.stats.defense * mult);
                stats.stamina += Math.floor(card.stats.stamina * mult);
                stats.speed += Math.floor(card.stats.speed * mult);
                stats.technique += Math.floor(card.stats.technique * mult);
            }
        });
        stats.total = stats.offense + stats.defense + stats.stamina + stats.speed + stats.technique;
        return stats;
    }, [deck, formation]);

    const getBasePower = (card) => {
        if (!card || !card.stats) return 0;
        return card.stats.offense + card.stats.defense + card.stats.stamina + card.stats.speed + card.stats.technique;
    };

    const handleSlotClick = (slotId) => setSelectedSlot(prev => prev === slotId ? null : slotId);

    const handleSellCard = async (card) => {
        // ★鈴木さん調整済みの価格設定！
        // ※ ここの価格は確認ダイアログの表示用。実際の決定はサーバー側。
        const sellPrices = { N: 10, R: 30, S: 50, SR: 100, SSR: 300 };
        const price = sellPrices[card.rarity?.id] || 10;
        
        const result = await Swal.fire({
            title: '移籍（売却）確認',
            html: `<b>${card.name}</b> (${rarityLabel(card)}) を移籍させますか？<br><br>移籍金: <b>${price} RBコイン</b>`,
            icon: 'warning', showCancelButton: true, confirmButtonText: '移籍する', confirmButtonColor: '#e65100', cancelButtonText: 'やめる'
        });
        
        if (!result.isConfirmed) return;
        
        try {
            // ★ 売却価格と所持確認はサーバー側で行う
            const fns = getFunctions(getApp(), 'asia-northeast1');
            const res = await httpsCallable(fns, 'sellCard')({ cardId: card.id });
            const gained = res?.data?.price ?? price;
            toast.success(`移籍金 ${gained} RBコインを獲得しました！`, { icon: '💰' });
        } catch (e) { 
            console.error('移籍エラー:', e?.code || '', e?.message || e);
            toast.error(e?.message || "エラーが発生しました"); 
        }
    };

    const handleCardSelect = (card) => {
        const currentFormationSlots = FORMATIONS[formation].map(s => s.id);
        const isUsed = currentFormationSlots.some(slotId => deck[slotId] && deck[slotId].id === card.id);

        if (!selectedSlot) {
            if (card.isMyCard || String(card.rarity?.id || '').startsWith('MY')) return toast('自分自身は移籍できません', { icon: '🛡️' });
            if (isUsed) return toast.error('スタメンに配置中のカードです！\n先にスタメンから外してください。');
            handleSellCard(card);
            return;
        }

        const isAlreadyPlaced = Object.entries(deck).find(([slot, c]) => c && c.id === card.id && currentFormationSlots.includes(slot));
        if (isAlreadyPlaced && isAlreadyPlaced[0] !== selectedSlot) {
            return toast.error('このカードは既に別のポジションに配置されています');
        }

        setDeck(prev => ({ ...prev, [selectedSlot]: card }));
        setSelectedSlot(null); 
        toast.success(`${card.name} を配置しました！`, { duration: 1500 });
    };

    const handleRemoveCard = (slotId, e) => {
        e.stopPropagation(); 
        setDeck(prev => {
            const newDeck = { ...prev };
            delete newDeck[slotId];
            return newDeck;
        });
    };

    const handleSaveDeck = async () => {
        setIsSaving(true);
        try {
            await updateDoc(doc(db, 'users', user.userId), { deck: deck, formation: formation });
            toast.success('最強のスタメンを保存しました！', { icon: '🔥' });
        } catch (e) { toast.error('保存失敗'); }
        finally { setIsSaving(false); }
    };

    const MiniCard = ({ card, slot }) => {
        const isSelected = selectedSlot === slot.id;
        
        if (!card) {
            return (
                <div 
                    onClick={() => handleSlotClick(slot.id)}
                    style={{
                        width: '55px', height: '75px', background: isSelected ? 'rgba(255,235,59,0.8)' : 'rgba(255,255,255,0.2)',
                        border: isSelected ? '3px solid #ffeb3b' : '2px dashed rgba(255,255,255,0.5)',
                        borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        cursor: 'pointer', transition: '0.2s', transform: isSelected ? 'scale(1.1)' : 'scale(1)'
                    }}
                >
                    <div style={{ fontSize: '10px', color: isSelected ? '#333' : 'white', fontWeight: 'bold' }}>{slot.label}</div>
                    <div style={{ fontSize: '24px', color: isSelected ? '#333' : 'white' }}>+</div>
                </div>
            );
        }

        const mult = getAptitudeMultiplier(card.position, slot.role, slot.label);
        const adjustedPower = Math.floor(getBasePower(card) * mult);
        
        let posColor = '#69f0ae'; 
        let powerColor = '#ffeb3b'; 
        let arrow = '';

        if (mult === 0.8) {
            posColor = '#ffb74d'; 
            arrow = '▼';
        } else if (mult <= 0.5) {
            posColor = '#ff5252'; 
            powerColor = '#ff5252'; 
            arrow = '▼▼';
        }

        return (
            <div 
                onClick={() => handleSlotClick(slot.id)}
                style={{
                    width: '55px', height: '75px', background: card.rarity.color,
                    border: isSelected ? '3px solid #ffeb3b' : '2px solid #222',
                    borderRadius: '8px', position: 'relative', display: 'flex', flexDirection: 'column',
                    alignItems: 'center', cursor: 'pointer', boxShadow: '0 4px 8px rgba(0,0,0,0.5)',
                    transition: '0.2s', transform: isSelected ? 'scale(1.1)' : 'scale(1)'
                }}
            >
                <div 
                    onClick={(e) => handleRemoveCard(slot.id, e)}
                    style={{ position: 'absolute', top: '-8px', right: '-8px', background: '#d32f2f', color: 'white', width: '20px', height: '20px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 'bold', border: '2px solid white', zIndex: 10 }}
                >
                    ×
                </div>
                
                <div style={{ background: 'rgba(0,0,0,0.6)', width: '100%', display: 'flex', justifyContent: 'space-between', padding: '2px 4px', borderTopLeftRadius: '6px', borderTopRightRadius: '6px', boxSizing: 'border-box' }}>
                    <span style={{ fontSize: '9px', color: 'white', fontWeight: 'bold' }}>{rarityLabel(card)}</span>
                    <span style={{ fontSize: '9px', fontWeight: 'bold', color: posColor }}>{card.position}</span>
                </div>
                
                <div style={{ width: '30px', height: '30px', borderRadius: '50%', background: '#555', marginTop: '2px', border: '2px solid white', overflow: 'hidden' }}>
                    {card.pictureUrl ? <img src={card.pictureUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/> : <span style={{fontSize:'18px', display:'block', textAlign:'center'}}>👤</span>}
                </div>
                <div style={{ fontSize: '8px', color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', width: '90%', textAlign: 'center', marginTop: '2px', textShadow: '1px 1px 1px black' }}>
                    {card.name}
                </div>
                
                <div style={{ background: '#222', width: '100%', textAlign: 'center', fontSize: '10px', color: powerColor, fontWeight: 'bold', position: 'absolute', bottom: 0, borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px' }}>
                    <span style={{fontSize:'8px'}}>{arrow}</span>{adjustedPower}
                </div>
            </div>
        );
    };

    return (
        <div style={{ minHeight: '100vh', background: '#1a1a1a', display: 'flex', flexDirection: 'column', color: 'white' }}>
            <Toaster position="top-center" />
            
            {/* ヘッダーエリア */}
            <div style={{ background: '#111', padding: '12px 15px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #333' }}>
                <button onClick={onBack} style={{ background: '#333', border: 'none', color: 'white', padding: '8px 15px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer' }}>← 戻る</button>
                <div style={{ display: 'flex', alignItems: 'center', background: '#000', borderRadius: '30px', padding: '4px 15px', border: '1px solid #444' }}>
                    <span style={{ fontSize: '11px', color: '#aaa', marginRight: '10px', fontWeight: 'bold' }}>陣形</span>
                    <select value={formation} onChange={(e) => { setFormation(e.target.value); setSelectedSlot(null); }} style={{ background: 'none', color: '#ffeb3b', border: 'none', fontWeight: 'bold', outline: 'none', cursor: 'pointer' }}>
                        {Object.keys(FORMATIONS).map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                </div>
            </div>

            {/* チームステータスエリア */}
            <div style={{ background: 'linear-gradient(180deg, #1565c0, #0d47a1)', padding: '15px', textAlign: 'center', boxShadow: '0 4px 10px rgba(0,0,0,0.5)', zIndex: 5 }}>
                <div style={{ fontSize: '11px', color: '#90caf9', fontWeight: 'bold', letterSpacing: '1px' }}>TEAM TOTAL POWER</div>
                <div style={{ fontSize: '38px', fontWeight: '900', color: '#ffeb3b', marginBottom: '10px', textShadow: '0 2px 5px rgba(0,0,0,0.5)' }}>{teamStats.total}</div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ background: 'rgba(0,0,0,0.3)', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', borderBottom: '2px solid #ff5252' }}>⚔️攻撃 {teamStats.offense}</div>
                    <div style={{ background: 'rgba(0,0,0,0.3)', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', borderBottom: '2px solid #448aff' }}>🛡️守備 {teamStats.defense}</div>
                    <div style={{ background: 'rgba(0,0,0,0.3)', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', borderBottom: '2px solid #69f0ae' }}>🏃‍♂️体力 {teamStats.stamina}</div>
                    <div style={{ background: 'rgba(0,0,0,0.3)', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', borderBottom: '2px solid #ffeb3b' }}>💨速度 {teamStats.speed}</div>
                    <div style={{ background: 'rgba(0,0,0,0.3)', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: 'bold', borderBottom: '2px solid #e040fb' }}>🎯技術 {teamStats.technique}</div>
                </div>
            </div>

            {/* フィールド（スタメン枠）エリア */}
            <div style={{ 
                flex: 1, background: '#2e7d32', backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 40px, rgba(255,255,255,0.05) 40px, rgba(255,255,255,0.05) 80px)',
                position: 'relative', overflow: 'hidden', padding: '15px 5px', borderBottom: '4px solid white', display: 'flex', flexDirection: 'column', justifyContent: 'space-around'
            }}>
                <div style={{ position:'absolute', top: '-50px', left: '50%', transform: 'translateX(-50%)', width: '200px', height: '100px', border: '3px solid rgba(255,255,255,0.4)' }}></div>
                <div style={{ position:'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', width: '100px', height: '50px', border: '3px solid rgba(255,255,255,0.4)' }}></div>
                <div style={{ position:'absolute', top: '25%', left: '0', width: '100%', height: '3px', background: 'rgba(255,255,255,0.4)' }}></div>
                
                {['FW', 'AMF', 'MF', 'DMF', 'DF', 'GK'].map(lineId => {
                    const slotsInLine = FORMATIONS[formation].filter(s => s.line === lineId);
                    if (slotsInLine.length === 0) return null;

                    return (
                        <div key={lineId} style={{ display: 'flex', justifyContent: 'space-evenly', position: 'relative', zIndex: 2 }}>
                            {slotsInLine.map(slot => (
                                <MiniCard key={slot.id} slot={slot} card={deck[slot.id]} />
                            ))}
                        </div>
                    );
                })}
            </div>

            {/* ガイダンスエリア */}
            {selectedSlot ? (
                <div style={{ background: '#ffeb3b', color: '#d32f2f', textAlign: 'center', padding: '10px', fontWeight: 'bold', fontSize: '12px', animation: 'pulse 1s infinite alternate' }}>
                    👇 下のリストから配置するカードを選んでください！
                </div>
            ) : (
                <div style={{ background: '#333', color: '#aaa', textAlign: 'center', padding: '8px', fontSize: '11px', borderBottom: '1px solid #444' }}>
                    💡 枠を選ばずにカードをタップすると「売却（移籍）」できます。
                </div>
            )}

            {/* 図鑑（アルバム）エリア */}
            <div style={{ height: '280px', background: '#222', padding: '15px 10px', overflowY: 'auto' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: 'white' }}>🎴 アルバム ({myCards.length}枚)</div>
                    <button onClick={handleSaveDeck} disabled={isSaving} style={{ background: '#e65100', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '20px', fontWeight: 'bold', cursor: 'pointer', boxShadow: '0 2px 5px rgba(0,0,0,0.5)' }}>
                        {isSaving ? '保存中...' : '💾 スタメンを保存'}
                    </button>
                </div>

                {/* ★ここに追加された「ソートボタン」！ */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '15px' }}>
                    {['rarity', 'position', 'power'].map(m => (
                        <button key={m} onClick={() => setSortMode(m)} style={{ 
                            flex: 1, background: sortMode === m ? '#1976d2' : '#444', 
                            border: sortMode === m ? '1px solid #64b5f6' : '1px solid #555', 
                            color: 'white', fontSize: '12px', padding: '8px 0', 
                            borderRadius: '20px', fontWeight: 'bold', cursor: 'pointer', transition: '0.2s' 
                        }}>
                            {m === 'rarity' ? '✨レア順' : m === 'position' ? '🏃ポジ順' : '💪パワー順'}
                        </button>
                    ))}
                </div>

                <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '10px' }}>
                    {myCards.map((card, idx) => {
                        const isUsed = Object.values(deck).some(c => c && c.id === card.id);
                        return (
                            <div key={idx} onClick={() => handleCardSelect(card)} style={{ flex: '0 0 75px', height: '105px', background: card.rarity.color, borderRadius: '8px', padding: '2px', position: 'relative', opacity: isUsed ? 0.4 : 1, cursor: 'pointer', boxShadow: '0 4px 8px rgba(0,0,0,0.5)' }}>
                                {isUsed && <div style={{ position:'absolute', top:'40%', left:'50%', transform:'translate(-50%, -50%)', color:'white', fontWeight:'bold', fontSize:'12px', textShadow:'1px 1px 2px black', zIndex:10 }}>配置済</div>}
                                <div style={{ background: '#111', height: '100%', borderRadius: '6px', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4px' }}>
                                    <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', fontSize: '9px', fontWeight: 'bold' }}>
                                        <span style={{ color: '#ffeb3b' }}>{rarityLabel(card)}</span>
                                        <span style={{ color: 'white' }}>{card.position}</span>
                                    </div>
                                    <div style={{ width: '35px', height: '35px', borderRadius: '50%', overflow: 'hidden', border: '1px solid #555', marginTop: '5px' }}>
                                        {card.pictureUrl ? <img src={card.pictureUrl} style={{width:'100%', height:'100%', objectFit:'cover'}} alt=""/> : <span style={{fontSize:'20px', display:'block', textAlign:'center', marginTop:'2px'}}>👤</span>}
                                    </div>
                                    <div style={{ fontSize: '9px', color:'white', fontWeight: 'bold', textAlign: 'center', marginTop: '5px', overflow: 'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis', width:'100%' }}>{card.name}</div>
                                    <div style={{ fontSize: '11px', color: '#ffeb3b', fontWeight: 'bold', marginTop: 'auto' }}>{getBasePower(card)}</div>
                                </div>
                            </div>
                        );
                    })}
                </div>
                
            </div>
            
            <style>
                {`
                @keyframes pulse {
                    0% { opacity: 0.7; }
                    100% { opacity: 1; }
                }
                ::-webkit-scrollbar {
                    height: 8px;
                }
                ::-webkit-scrollbar-thumb {
                    background: #555;
                    border-radius: 4px;
                }
                `}
            </style>
        </div>
    );
};

export default DeckBuilder;